import { TrendingUp, TrendingDown, Minus, AlertCircle, ArrowUpRight, ArrowDownRight, Trash2, MoreVertical, ChevronDown, ChevronUp, Pencil, X, Check, Loader2, MessageSquare, Send, Trash } from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPICard = ({ 
  title, 
  value, 
  unit = '', 
  planValue, 
  variance, 
  ragStatus = 'no_plan',
  trend = [],
  yoy = null,
  className,
  kpiId = null,
  onDelete = null,
  canEdit = false,
  // Period context for inline editing
  periodType = null,
  periodStartDate = null,
  periodEndDate = null,
  year = null,
  functionName = null,
  segment = 'All',
  // Callback when data is updated
  onDataUpdate = null,
  // Last updated info
  updatedBy = null,
  updatedAt = null,
  // Weekly pacing props
  isPacedWeekly = false,
  pacing = null,
  actualToDate = null,
  expectedToDate = null,
  monthlyPlan = null,
  weeklyPlan = null,  // Monthly plan / 4
  paceStatus = null,
  pacingPercentage = null,
  // Segment breakdown props
  segmentBreakdown = null,
  // Comment count (passed from parent)
  commentCount = 0
}) => {
  const { user } = useAuth();
  const [showSegments, setShowSegments] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editValues, setEditValues] = useState({
    actual_value: value ?? '',
    plan_value: planValue ?? '',
    notes: ''
  });
  
  // Segment editing state
  const [editingSegment, setEditingSegment] = useState(null);
  const [segmentEditValue, setSegmentEditValue] = useState('');
  const [savingSegment, setSavingSegment] = useState(false);
  
  // Comment state
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [localCommentCount, setLocalCommentCount] = useState(commentCount);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  
  // Update local count when prop changes
  useEffect(() => {
    setLocalCommentCount(commentCount);
  }, [commentCount]);
  
  const canComment = user && (user.role === 'admin' || user.role === 'elt');
  
  const formatValue = (val, unitType) => {
    if (val === null || val === undefined) return '—';
    
    if (unitType === '$M') {
      return `$${val.toFixed(1)}M`;
    }
    if (unitType === '$') {
      return `$${val.toLocaleString()}`;
    }
    if (unitType === '%') {
      return `${val.toFixed(1)}%`;
    }
    if (unitType === '#') {
      return val.toLocaleString();
    }
    return `${val} ${unitType}`;
  };

  const getRAGStyles = (status) => {
    switch (status) {
      case 'green':
      case 'on_pace':
        return { bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', text: 'text-[#065F46]', icon: TrendingUp };
      case 'yellow':
      case 'at_risk':
        return { bg: 'bg-[#FFFBEB]', border: 'border-[#FDE68A]', text: 'text-[#92400E]', icon: Minus };
      case 'red':
      case 'off_pace':
        return { bg: 'bg-[#FEF2F2]', border: 'border-[#FECACA]', text: 'text-[#991B1B]', icon: TrendingDown };
      case 'no_data':
        return { bg: 'bg-[#F1F5F9]', border: 'border-[#CBD5E1]', text: 'text-[#64748B]', icon: AlertCircle };
      default:
        return { bg: 'bg-[#F3F4F6]', border: 'border-[#E5E7EB]', text: 'text-[#6B7280]', icon: AlertCircle };
    }
  };

  // Use paceStatus for weekly paced KPIs, otherwise use ragStatus
  const effectiveStatus = isPacedWeekly && paceStatus ? paceStatus : ragStatus;
  const ragStyles = getRAGStyles(effectiveStatus);
  const StatusIcon = ragStyles.icon;
  const isNoData = ragStatus === 'no_data';
  
  const getPaceStatusLabel = (status) => {
    switch (status) {
      case 'on_pace': return 'On Pace';
      case 'at_risk': return 'At Risk';
      case 'off_pace': return 'Off Pace';
      default: return null;
    }
  };

  const varianceDisplay = variance !== null && variance !== undefined
    ? `${variance >= 0 ? '+' : ''}${(variance * 100).toFixed(1)}%`
    : null;

  const yoyDisplay = yoy?.has_yoy && yoy?.yoy_change !== null
    ? `${yoy.yoy_change >= 0 ? '+' : ''}${(yoy.yoy_change * 100).toFixed(1)}%`
    : null;

  const handleStartEdit = () => {
    setEditValues({
      actual_value: value ?? '',
      plan_value: planValue ?? '',
      notes: ''
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValues({
      actual_value: value ?? '',
      plan_value: planValue ?? '',
      notes: ''
    });
  };

  const handleSave = async () => {
    // Validate at least one field has changed
    const actualChanged = editValues.actual_value !== '' && editValues.actual_value !== value;
    const planChanged = editValues.plan_value !== '' && editValues.plan_value !== planValue;
    const notesAdded = editValues.notes.trim() !== '';
    
    if (!actualChanged && !planChanged && !notesAdded) {
      toast.info('No changes to save');
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        metric_name: title,
        function: functionName,
        period_type: periodType,
        period_start_date: periodStartDate,
        period_end_date: periodEndDate,
        year: year,
        segment: segment
      };

      // Only include changed fields
      if (editValues.actual_value !== '' && editValues.actual_value !== value) {
        payload.actual_value = parseFloat(editValues.actual_value);
      }
      if (editValues.plan_value !== '' && editValues.plan_value !== planValue && periodType !== 'weekly') {
        payload.plan_value = parseFloat(editValues.plan_value);
      }
      if (editValues.notes.trim()) {
        payload.notes = editValues.notes.trim();
      }

      const response = await axios.post(`${API}/kpi-values/inline-update`, payload);
      
      toast.success(`${title} ${response.data.action} successfully`);
      setIsEditing(false);
      
      // Trigger refresh of parent dashboard
      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if editing is allowed (need period context)
  const canInlineEdit = canEdit && periodType && periodStartDate && year && functionName;

  // ============ COMMENT FUNCTIONS ============
  const fetchComments = async () => {
    if (!periodStartDate || !functionName || !title) return;
    
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API}/comments`, {
        params: {
          metric_name: title,
          function: functionName,
          year: year,
          period_type: periodType,
          period_start_date: periodStartDate
        }
      });
      setComments(response.data.comments || []);
      setLocalCommentCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleToggleComments = () => {
    if (!showComments && comments.length === 0) {
      fetchComments();
    }
    setShowComments(!showComments);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      const response = await axios.post(`${API}/comments`, {
        metric_name: title,
        function: functionName,
        year: year,
        period_type: periodType,
        period_start_date: periodStartDate,
        text: newComment.trim()
      });
      
      setComments(prev => [...prev, response.data.comment]);
      setLocalCommentCount(prev => prev + 1);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    
    try {
      await axios.put(`${API}/comments/${commentId}`, {
        text: editCommentText.trim()
      });
      
      setComments(prev => prev.map(c => 
        c.id === commentId ? { ...c, text: editCommentText.trim() } : c
      ));
      setEditingCommentId(null);
      setEditCommentText('');
      toast.success('Comment updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      setLocalCommentCount(prev => Math.max(0, prev - 1));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const formatCommentDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  // ============================================

  // ============ SEGMENT EDITING FUNCTIONS ============
  const handleStartSegmentEdit = (segmentName, currentValue) => {
    setEditingSegment(segmentName);
    setSegmentEditValue(currentValue ?? '');
  };

  const handleCancelSegmentEdit = () => {
    setEditingSegment(null);
    setSegmentEditValue('');
  };

  const handleSaveSegment = async () => {
    if (segmentEditValue === '' || editingSegment === null) {
      toast.info('Please enter a value');
      return;
    }

    setSavingSegment(true);
    try {
      // For YTD or Yearly views, save segment data as monthly for the current month
      // Segment data is always stored at the monthly level and aggregated for YTD/Yearly views
      let savePeriodType = periodType;
      let savePeriodStart = periodStartDate;
      let savePeriodEnd = periodEndDate;
      
      if (periodType === 'ytd' || periodType === 'yearly') {
        // Save as current month
        const now = new Date();
        const monthNum = String(now.getMonth() + 1).padStart(2, '0');
        const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
        savePeriodType = 'monthly';
        savePeriodStart = `${year}-${monthNum}-01`;
        savePeriodEnd = `${year}-${monthNum}-${lastDay}`;
      } else if (periodType === 'quarterly') {
        // For quarterly, save as the last month of the quarter
        const quarterEndMonths = { 'Q1': '03', 'Q2': '06', 'Q3': '09', 'Q4': '12' };
        // Extract quarter from periodStartDate (e.g., 2026-01-01 = Q1)
        const startMonth = parseInt(periodStartDate.substring(5, 7));
        const quarter = Math.ceil(startMonth / 3);
        const endMonth = quarterEndMonths[`Q${quarter}`];
        const lastDay = new Date(year, parseInt(endMonth), 0).getDate();
        savePeriodType = 'monthly';
        savePeriodStart = `${year}-${endMonth}-01`;
        savePeriodEnd = `${year}-${endMonth}-${lastDay}`;
      }
      
      const payload = {
        metric_name: title,
        function: functionName,
        period_type: savePeriodType,
        period_start_date: savePeriodStart,
        period_end_date: savePeriodEnd,
        year: year,
        segment: editingSegment,
        actual_value: parseFloat(segmentEditValue)
      };

      const response = await axios.post(`${API}/kpi-values/inline-update`, payload);
      
      toast.success(`${editingSegment} updated successfully`);
      setEditingSegment(null);
      setSegmentEditValue('');
      
      // Trigger refresh of parent dashboard
      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error) {
      console.error('Segment save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save segment value');
    } finally {
      setSavingSegment(false);
    }
  };

  const canEditSegments = canEdit && periodType && periodStartDate && year && functionName;
  // ====================================================

  return (
    <div 
      className={cn(
        "bg-white rounded-lg border border-[#E5E7EB] p-4 sm:p-6 kpi-card relative group",
        isEditing && "ring-2 ring-[#2563EB] ring-offset-2",
        className
      )}
      data-testid={`kpi-card-${title?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Actions Menu */}
      {canEdit && !isEditing && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          {canInlineEdit && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7"
              onClick={handleStartEdit}
              data-testid={`edit-kpi-${title?.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Pencil className="w-4 h-4 text-[#2563EB]" />
            </Button>
          )}
          {onDelete && kpiId && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <MoreVertical className="w-4 h-4 text-[#6B7280]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  className="text-[#991B1B] focus:text-[#991B1B] cursor-pointer"
                  onClick={() => onDelete(kpiId, title)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete KPI
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      {/* Edit Mode Actions */}
      {isEditing && (
        <div className="absolute top-2 right-2 flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 text-[#6B7280] hover:text-[#991B1B]"
            onClick={handleCancelEdit}
            disabled={isSaving}
          >
            <X className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 text-[#2563EB] hover:text-[#1D4ED8]"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-2 sm:mb-3">
        <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wider truncate pr-8">
          {title}
        </p>
        {!isEditing && (
          <div className={cn(
            "w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0",
            ragStyles.bg
          )}>
            <StatusIcon className={cn("w-3 h-3 sm:w-4 sm:h-4", ragStyles.text)} />
          </div>
        )}
      </div>

      {/* Edit Mode */}
      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#6B7280] mb-1 block">Actual Value ({unit})</label>
            <Input
              type="number"
              step="any"
              value={editValues.actual_value}
              onChange={(e) => setEditValues(prev => ({ ...prev, actual_value: e.target.value }))}
              placeholder="Enter actual value"
              className="h-9"
              data-testid="edit-actual-value"
            />
          </div>
          {periodType !== 'weekly' && (
            <div>
              <label className="text-xs text-[#6B7280] mb-1 block">Plan Value ({unit})</label>
              <Input
                type="number"
                step="any"
                value={editValues.plan_value}
                onChange={(e) => setEditValues(prev => ({ ...prev, plan_value: e.target.value }))}
                placeholder="Enter plan value"
                className="h-9"
                data-testid="edit-plan-value"
              />
            </div>
          )}
          <div>
            <label className="text-xs text-[#6B7280] mb-1 block">Add Note (optional)</label>
            <Textarea
              value={editValues.notes}
              onChange={(e) => setEditValues(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add a note about this change..."
              className="h-16 resize-none text-sm"
              data-testid="edit-notes"
            />
          </div>
        </div>
      ) : (
        <>
          {/* Value */}
          <div className="mb-2 sm:mb-3">
            {isNoData ? (
              <div className="py-2">
                <p className="text-sm sm:text-base text-[#64748B] italic">
                  No data uploaded yet
                </p>
                {canInlineEdit && (
                  <button 
                    onClick={handleStartEdit}
                    className="text-xs text-[#2563EB] hover:underline mt-1"
                  >
                    Click to enter data
                  </button>
                )}
              </div>
            ) : isPacedWeekly && actualToDate !== null ? (
              // Weekly Pacing Display
              <div>
                <p className="text-xl sm:text-3xl font-bold text-[#111827] tracking-tight tabular-nums">
                  {formatValue(actualToDate, unit)}
                </p>
                <p className="text-xs text-[#6B7280]">Actual to date</p>
              </div>
            ) : (
              <p className="text-xl sm:text-3xl font-bold text-[#111827] tracking-tight tabular-nums">
                {formatValue(value, unit)}
              </p>
            )}
          </div>

          {/* Weekly Pacing Info - Show for paced weekly KPIs */}
          {!isNoData && isPacedWeekly && expectedToDate !== null && (
            <div className="space-y-2 mb-2">
              {/* Weekly Plan (Monthly / 4) */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#6B7280]">Weekly plan:</span>
                <span className="font-semibold text-[#2563EB]">{formatValue(weeklyPlan, unit)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#6B7280]">Expected to date:</span>
                <span className="font-medium text-[#111827]">{formatValue(expectedToDate, unit)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#6B7280]">Monthly plan:</span>
                <span className="font-medium text-[#9CA3AF]">{formatValue(monthlyPlan, unit)}</span>
              </div>
              {pacingPercentage !== null && (
                <div className="w-full bg-[#E5E7EB] rounded-full h-1.5 mt-1">
                  <div 
                    className={cn(
                      "h-1.5 rounded-full transition-all",
                      paceStatus === 'on_pace' ? 'bg-[#10B981]' :
                      paceStatus === 'at_risk' ? 'bg-[#F59E0B]' :
                      'bg-[#EF4444]'
                    )}
                    style={{ width: `${Math.min(pacingPercentage, 100)}%` }}
                  />
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className={cn("text-xs font-semibold px-2 py-0.5 rounded", ragStyles.bg, ragStyles.text)}>
                  {getPaceStatusLabel(paceStatus)}
                </span>
                {pacingPercentage !== null && (
                  <span className="text-xs text-[#6B7280]">
                    {pacingPercentage.toFixed(0)}% of pace
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Plan & Variance - Hide when no data or when showing weekly pacing */}
          {!isNoData && !isPacedWeekly && (
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap text-xs sm:text-sm">
              {planValue !== null && planValue !== undefined && (
                <span className="text-[#9CA3AF]">
                  Plan: {formatValue(planValue, unit)}
                </span>
              )}
              {varianceDisplay && (
                <span className={cn(
                  "font-medium",
                  ragStyles.text
                )}>
                  {varianceDisplay}
                </span>
              )}
              {ragStatus === 'no_plan' && !yoyDisplay && (
                <span className="text-[#9CA3AF]">No plan</span>
              )}
            </div>
          )}

          {/* Upload prompt for no data */}
          {isNoData && !canInlineEdit && (
            <div className="text-xs text-[#94A3B8]">
              Upload data to see metrics
            </div>
          )}

          {/* YoY Comparison - Hide when no data */}
          {!isNoData && yoyDisplay && (
            <div className="mt-1.5 sm:mt-2 flex items-center gap-1 sm:gap-1.5">
              {yoy.yoy_change >= 0 ? (
                <ArrowUpRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#059669]" />
              ) : (
                <ArrowDownRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#DC2626]" />
              )}
              <span className={cn(
                "text-[10px] sm:text-xs font-medium",
                yoy.yoy_change >= 0 ? "text-[#059669]" : "text-[#DC2626]"
              )}>
                {yoyDisplay} YoY
              </span>
            </div>
          )}

          {/* Last Updated Info */}
          {updatedBy && (
            <div className="mt-2 pt-2 border-t border-[#F3F4F6]">
              <p className="text-[10px] text-[#9CA3AF]">
                Last updated by {updatedBy}
                {updatedAt && ` • ${new Date(updatedAt).toLocaleDateString()}`}
              </p>
            </div>
          )}

          {/* Mini Trend Line - Hidden on mobile and when no data */}
          {!isNoData && trend && trend.length > 1 && (
            <div className="hidden sm:block mt-4 pt-4 border-t border-[#F3F4F6]">
              <div className="flex items-end gap-1 h-8">
                {trend.slice(-6).map((point, idx) => {
                  const maxVal = Math.max(...trend.map(t => t.value));
                  const minVal = Math.min(...trend.map(t => t.value));
                  const range = maxVal - minVal || 1;
                  const height = ((point.value - minVal) / range) * 100;
                  return (
                    <div
                      key={idx}
                      className="flex-1 bg-[#E5E7EB] rounded-sm hover:bg-[#2563EB] transition-colors"
                      style={{ height: `${Math.max(height, 10)}%` }}
                      title={`${point.date}: ${point.value}`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Segment Breakdown for Brokerage KPIs */}
          {segmentBreakdown?.segments_enabled && Object.keys(segmentBreakdown.segments || {}).length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
              <button
                onClick={() => setShowSegments(!showSegments)}
                className="flex items-center gap-1 text-xs text-[#6B7280] hover:text-[#2563EB] transition-colors w-full"
              >
                {showSegments ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                <span className="font-medium">Segment Breakdown</span>
                {canEditSegments && (
                  <span className="ml-auto text-[10px] text-[#9CA3AF]">
                    {showSegments ? '' : 'Click to edit'}
                  </span>
                )}
              </button>
              
              {showSegments && (
                <div className="mt-2 space-y-1.5">
                  {Object.entries(segmentBreakdown.segments).map(([seg, data]) => (
                    <div key={seg} className="flex items-center justify-between text-xs group/segment">
                      <span className="text-[#6B7280] truncate flex-1">{seg}</span>
                      
                      {/* Editing Mode for Segment */}
                      {editingSegment === seg ? (
                        <div className="flex items-center gap-1 ml-2">
                          <Input
                            type="number"
                            step="any"
                            value={segmentEditValue}
                            onChange={(e) => setSegmentEditValue(e.target.value)}
                            className="h-6 w-24 text-xs px-2"
                            placeholder="Value"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveSegment();
                              if (e.key === 'Escape') handleCancelSegmentEdit();
                            }}
                            data-testid={`segment-input-${seg.toLowerCase().replace(/\s+/g, '-')}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-[#6B7280] hover:text-[#991B1B]"
                            onClick={handleCancelSegmentEdit}
                            disabled={savingSegment}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-[#2563EB] hover:text-[#1D4ED8]"
                            onClick={handleSaveSegment}
                            disabled={savingSegment}
                          >
                            {savingSegment ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 ml-2">
                          <span className={cn(
                            "font-medium",
                            data.has_data ? "text-[#111827]" : "text-[#CBD5E1]"
                          )}>
                            {data.has_data ? formatValue(data.actual_value, unit) : '—'}
                          </span>
                          {/* Edit button for segment - shows on hover */}
                          {canEditSegments && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 opacity-0 group-hover/segment:opacity-100 transition-opacity"
                              onClick={() => handleStartSegmentEdit(seg, data.actual_value)}
                              data-testid={`edit-segment-${seg.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <Pencil className="w-3 h-3 text-[#2563EB]" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {segmentBreakdown.total !== null && (
                    <div className="flex items-center justify-between text-xs pt-1.5 border-t border-dashed border-[#E5E7EB]">
                      <span className="text-[#6B7280] font-medium">Total</span>
                      <span className="font-semibold text-[#111827]">{formatValue(segmentBreakdown.total, unit)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Comments Section - Only show if there are comments OR user can comment */}
          {(localCommentCount > 0 || canComment) && periodStartDate && functionName && (
            <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
              <button
                onClick={handleToggleComments}
                className="flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#2563EB] transition-colors w-full"
                data-testid={`comments-toggle-${title?.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span className="font-medium">
                  {localCommentCount > 0 
                    ? `${localCommentCount} Comment${localCommentCount !== 1 ? 's' : ''}`
                    : 'Add Comment'
                  }
                </span>
                {showComments ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
              </button>
              
              {showComments && (
                <div className="mt-3 space-y-3">
                  {/* Loading State */}
                  {loadingComments && (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#6B7280]" />
                    </div>
                  )}
                  
                  {/* Comments List */}
                  {!loadingComments && comments.length > 0 && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {comments.map((comment) => (
                        <div 
                          key={comment.id} 
                          className="bg-[#F9FAFB] rounded-md p-2.5 text-xs"
                          data-testid={`comment-${comment.id}`}
                        >
                          {editingCommentId === comment.id ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editCommentText}
                                onChange={(e) => setEditCommentText(e.target.value)}
                                className="h-16 text-xs resize-none"
                                placeholder="Edit your comment..."
                              />
                              <div className="flex gap-1 justify-end">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-6 px-2 text-xs"
                                  onClick={() => {
                                    setEditingCommentId(null);
                                    setEditCommentText('');
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="h-6 px-2 text-xs bg-[#2563EB]"
                                  onClick={() => handleEditComment(comment.id)}
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-[#374151] whitespace-pre-wrap break-words flex-1">
                                  {comment.text}
                                </p>
                                {/* Edit/Delete for author or admin */}
                                {user && (user.id === comment.author_id || user.role === 'admin') && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-5 w-5 flex-shrink-0">
                                        <MoreVertical className="w-3 h-3 text-[#9CA3AF]" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-24">
                                      <DropdownMenuItem
                                        className="text-xs cursor-pointer"
                                        onClick={() => {
                                          setEditingCommentId(comment.id);
                                          setEditCommentText(comment.text);
                                        }}
                                      >
                                        <Pencil className="w-3 h-3 mr-1.5" />
                                        Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="text-xs text-[#DC2626] cursor-pointer"
                                        onClick={() => handleDeleteComment(comment.id)}
                                      >
                                        <Trash className="w-3 h-3 mr-1.5" />
                                        Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1.5 text-[10px] text-[#9CA3AF]">
                                <span className="font-medium text-[#6B7280]">{comment.author_name}</span>
                                <span>•</span>
                                <span>{formatCommentDate(comment.created_at)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* No comments message */}
                  {!loadingComments && comments.length === 0 && (
                    <p className="text-[10px] text-[#9CA3AF] text-center py-1">
                      No comments yet
                    </p>
                  )}
                  
                  {/* Add Comment Form - Only for Admin/ELT */}
                  {canComment && (
                    <div className="flex gap-2 items-end">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="h-16 text-xs resize-none flex-1"
                        data-testid={`new-comment-input-${title?.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <Button
                        size="sm"
                        className="h-8 w-8 p-0 bg-[#2563EB] hover:bg-[#1D4ED8]"
                        onClick={handleAddComment}
                        disabled={submittingComment || !newComment.trim()}
                        data-testid={`submit-comment-${title?.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {submittingComment ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default KPICard;
